import type { TFunction } from "i18next";

/**
 * Canonical re-authentication markers returned by sprint-api in 401 `detail`
 * strings. These are a stable contract between the API and the UI; never
 * rewrite them client-side, and never rely on substring matching.
 */
export const REAUTH_MARKERS = {
  score: "score_auth_required",
  emjogo: "emjogo_auth_required",
} as const;

export type ReauthMarker = (typeof REAUTH_MARKERS)[keyof typeof REAUTH_MARKERS];

export interface PydanticFieldError {
  loc: Array<string | number>;
  msg: string;
  type: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
}

/**
 * Structured error returned by POST /matches/{id}/sheet/submit-to-score when
 * one or more selected players don't have a valid Score license.
 */
export interface InvalidLicenseDetail {
  code: "invalid_license";
  message: string;
  players: Array<{
    player_id?: number;
    name?: string;
    number?: string | number | null;
    license?: string | null;
    reason?: string;
  }>;
}

export type ApiDetail =
  | string
  | PydanticFieldError[]
  | InvalidLicenseDetail
  | Record<string, unknown>
  | undefined;

/**
 * Base error thrown by the API client for any non-2xx HTTP response. Includes
 * the parsed body (when JSON) so UI code can branch on structured detail.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: ApiDetail;
  readonly body: unknown;
  readonly url: string;

  constructor(
    message: string,
    params: { status: number; detail: ApiDetail; body: unknown; url: string },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = params.status;
    this.detail = params.detail;
    this.body = params.body;
    this.url = params.url;
  }

  isReauthRequired(): ReauthMarker | null {
    if (this.status !== 401) return null;
    if (typeof this.detail !== "string") return null;
    if (this.detail === REAUTH_MARKERS.score) return REAUTH_MARKERS.score;
    if (this.detail === REAUTH_MARKERS.emjogo) return REAUTH_MARKERS.emjogo;
    return null;
  }

  isInvalidLicense(): InvalidLicenseDetail | null {
    if (this.status !== 400) return null;
    if (!this.detail || typeof this.detail !== "object") return null;
    if (Array.isArray(this.detail)) return null;
    const detail = this.detail as Record<string, unknown>;
    if (detail.code === "invalid_license") {
      return this.detail as unknown as InvalidLicenseDetail;
    }
    return null;
  }

  /**
   * Pydantic validation errors (POST /jobs params, request body 422s).
   */
  isValidationError(): PydanticFieldError[] | null {
    if (this.status !== 422) return null;
    if (!Array.isArray(this.detail)) return null;
    return this.detail as PydanticFieldError[];
  }
}

/**
 * Thrown when the network request fails entirely (offline, DNS, aborted).
 */
export class NetworkError extends Error {
  readonly cause: unknown;
  readonly url: string;
  constructor(url: string, cause: unknown) {
    super(`Network request failed: ${url}`);
    this.name = "NetworkError";
    this.url = url;
    this.cause = cause;
  }
}

export function isReauthError(error: unknown): ReauthMarker | null {
  if (error instanceof ApiError) return error.isReauthRequired();
  return null;
}

export function isReauthMarker(value: string): value is ReauthMarker {
  return value === REAUTH_MARKERS.score || value === REAUTH_MARKERS.emjogo;
}

/**
 * Translates an error into a user-facing message using the global `errors.*`
 * keys. Never surfaces raw `error.detail` text — users are non-technical and
 * couldn't act on it anyway, so unclassified statuses get a generic message
 * instead of whatever string the upstream service happened to return.
 *
 * Callers with domain-specific meaning for 401/403 (e.g. login, where a 401
 * means "wrong password" rather than "session expired") should check those
 * statuses themselves before falling back to this helper.
 */
export function getErrorBody(error: unknown, t: TFunction): string {
  if (error instanceof NetworkError) return t("errors.networkBody");
  if (error instanceof ApiError) {
    if (error.status === 401) return t("errors.unauthorizedBody");
    if (error.status === 403) return t("errors.forbiddenBody");
    if (error.status >= 500) return t("errors.genericBody");
    return t("errors.networkBody");
  }
  return t("errors.genericBody");
}
